import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  CircularProgress,
  Grid,
  Tab,
  Typography,
} from "@mui/material";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import MainUserInfo from "./components/MainUserInfo";
import UserProfessionalActivities from "./components/UserProfessionalActivities";
import UserEducationDocuments from "./components/UserEducationDocuments";
import UserScientificWorks from "./components/UserScientificWorks";
import UserSettings from "./components/UserSettings";
import { SuittService } from "../../../services";
import { useParams } from "react-router-dom";
import { ErrorFallback, Page } from "../../../components";
import { ErrorBoundary } from "react-error-boundary";

function UserEdit() {
  const suittService = useMemo(() => new SuittService(), []);
  const [activeProfileTab, setActiveProfileTab] = useState(0);
  const { id: userId } = useParams();
  const [user, setUser] = useState({
    data: null,
    loading: true,
  });
  const didFetchRef = useRef(false);

  const getUserInfo = useCallback(async () => {
    const result = await suittService.getUserInfo(userId);
    setTimeout(() => {
      if (!result.data) return setUser({ loading: false });
      setUser({ data: result.data, loading: false });
    }, 500);
  }, [suittService, userId]);

  useEffect(() => {
    if (didFetchRef.current) return;
    didFetchRef.current = true;
    (async () => getUserInfo())();
  }, [getUserInfo]);

  const handleProfileTabChange = (event, newActiveTab) => {
    setActiveProfileTab(newActiveTab);
  };

  if (user.loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", marginTop: 3 }}>
        <CircularProgress color="secondary" />
      </Box>
    );
  }

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <Page title="Особистий профіль - редагувати" backTo={`/cabinet/users/${userId}`}>
        <Grid container>
          <Grid item xs={12} sm={12} md={12} lg={12}>
            {user.data ? (
              <TabContext value={activeProfileTab} sx={{ width: "100%" }}>
                <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                  <TabList
                    value={activeProfileTab}
                    onChange={handleProfileTabChange}
                    scrollButtons="auto"
                    variant="scrollable"
                  >
                    <Tab label={"Основні данні"} value={0} />
                    <Tab
                      label={"Досягнення у професійній діяльності"}
                      value={1}
                    />
                    <Tab label={"Документи про освіту"} value={2} />
                    {[
                      "Teacher",
                      "ViceRectorForScientificWork",
                      "ViceRectorForEducationalWork",
                      "DeanOfTheFaculty",
                      "HeadOfTheDepartment",
                    ].entries((type) => type === user.data.accountType) && (
                      <Tab
                        label={"Наукові та навчально-методичні праці"}
                        value={3}
                      />
                    )}
                    <Tab label={"Налаштування"} value={4} />
                  </TabList>
                </Box>
                <TabPanel value={0}>
                  <MainUserInfo user={user.data} getUserInfo={getUserInfo} />
                </TabPanel>
                <TabPanel value={1}>
                  <UserProfessionalActivities
                    user={user.data}
                    getUserInfo={getUserInfo}
                  />
                </TabPanel>
                <TabPanel value={2}>
                  <UserEducationDocuments
                    user={user.data}
                    getUserInfo={getUserInfo}
                  />
                </TabPanel>
                <TabPanel value={3}>
                  <UserScientificWorks user={user.data} />
                </TabPanel>
                <TabPanel value={4}>
                  <UserSettings user={user.data} />
                </TabPanel>
              </TabContext>
            ) : (
              <Grid item xs={12} sm={12} md={12} lg={12}></Grid>
            )}
          </Grid>
        </Grid>
      </Page>
    </ErrorBoundary>
  );
}

export default UserEdit;
